import { LayoutBase } from './LayoutBase';
import { toRadian } from '../utils/string.utilts';

export default class extends LayoutBase {
  getSize(): { width: number; height: number } {
    const { ref } = this.dataItem as IDataItemArcProgressBar;
    const refImg = this.runtime.getDataItem<IImage>(ref)?.imgEl;
    return {
      width: refImg?.width ?? 0,
      height: refImg?.height ?? 0
    };
  }
  private getAngleRange(node: IDataItemArcProgressBar) {
    const data = this.getDataSourceValue(node.source) as number;
    // canvas绘制弧形的0度是水平（90度）的位置，与实际不符，所有这里-90处理
    // 扫描起始角
    const angleStart = (node.angleStart - 90) % 360;

    const angleRange = Number(node.angleRange);

    // 起始值
    const { valueStart, valueRange } = this.getValueStartRange();

    // 首先判断数值超出范围的情况
    if (data < valueStart) {
      return {
        angleStart,
        angleEnd: angleStart,
        isLessThan: true
      };
    } else if (data >= valueRange + valueStart) {
      return {
        angleStart,
        angleEnd: angleStart + angleRange,
        isMoreThan: true
      };
    } else {
      // 进度比例
      const ratio = angleRange / valueRange;
      // 扫描结束角
      const angleEnd = angleStart + (data - valueStart) * ratio;
      return {
        angleStart,
        angleEnd
      };
    }
  }
  private drawOffscreenArc(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    angleStart: number,
    angleEnd: number
  ) {
    const { pivotX, pivotY, barRadius, barWidth, endingStyle, angleRange } =
      this.dataItem as IDataItemArcProgressBar;
    const r = barRadius;
    if (endingStyle === 'round') ctx.lineCap = 'round';
    ctx.globalCompositeOperation = 'destination-in';
    ctx.lineWidth = barWidth;
    ctx.beginPath();
    ctx.arc(
      pivotX,
      pivotY,
      r,
      toRadian(angleStart),
      toRadian(angleEnd),
      Number(angleRange) < 0
    );

    ctx.stroke();
  }
  draw(): void {
    const { ctx, runtime, dataItem, _alpha = 1 } = this;
    ctx.save();

    const node = dataItem as IDataItemArcProgressBar;
    const {
      ref,
      bg,
      indicatorImage,
      pivotX,
      pivotY,
      indicatorRadius,
      barRadius,
      source,
      angleRange,
      barWidth,
      endingStyle
    } = node;

    // 背景
    const { imgEl: bgImageEl, src: bgImageSrc } =
      runtime.getDataItem<IImage>(bg) ?? {};
    const { width: bgImageElWidth = 0, height: bgImageElHeight = 0 } =
      bgImageEl ?? {};

    // 进度条
    const { imgEl: progressImageEL, src: progressImageSrc } =
      runtime.getDataItem<IImage>(ref) ?? {};
    const {
      width: progressImageELWidth = 0,
      height: progressImageELHeight = 0
    } = progressImageEL ?? {};

    // 指示器
    const { imgEl: indicatorImageEl, src: indicatorImageSrc } =
      runtime.getDataItem<IImage>(indicatorImage) ?? {};
    const {
      width: indicatorImageElWidth = 0,
      height: indicatorImageElHeight = 0
    } = indicatorImageEl ?? {};

    const widthMax = Math.max(
      bgImageElWidth,
      progressImageELWidth,
      indicatorImageElWidth
    );
    const heightMax = Math.max(
      bgImageElHeight,
      progressImageELHeight,
      indicatorImageElHeight
    );

    // 透明度
    ctx.globalAlpha = _alpha;

    const { canvas: offscreenCanvas } = this.getOffscreen(
      {
        width: widthMax,
        height: heightMax,
        init: (_, ctx) => {
          // 绘制背景
          if (bgImageEl) {
            const { canvas: offscreenCanvas1 } = this.getOffscreen(
              {
                width: bgImageElWidth,
                height: bgImageElHeight,
                init: (_, ctx) => {
                  this.drawRecolorImage(ctx, bgImageEl, {
                    x: 0,
                    y: 0
                  });
                },
                dirtyParmas: {
                  bgImageSrc: bgImageSrc ?? ''
                }
              },
              'bg'
            );
            ctx.drawImage(offscreenCanvas1, 0, 0);
          }
          // 进度条起始、结束角度
          const {
            angleStart,
            angleEnd,
            isLessThan = false
          } = this.getAngleRange(node);

          // 绘制进度条
          if (progressImageEL) {
            const { canvas: offscreenCanvas2 } = this.getOffscreen(
              {
                width: progressImageELWidth,
                height: progressImageELHeight,
                init: (_, ctx) => {
                  if (!isLessThan)
                    this.drawRecolorImage(ctx, progressImageEL, {
                      x: 0,
                      y: 0
                    });
                  // 绘制进度条
                  if (source) {
                    this.drawOffscreenArc(ctx, angleStart, angleEnd);
                  } else {
                    // // 当没有数据源时，需要添加示意的进度条
                    const { angleRange, angleStart } = node;
                    const _start = angleStart - 90;
                    const _end = _start + angleRange;
                    this.drawOffscreenArc(ctx, _start, _end);
                  }
                },
                dirtyParmas: {
                  progressImageSrc: progressImageSrc ?? '',
                  source: source ?? '',
                  angleStart,
                  angleEnd,
                  angleRange,
                  barRadius,
                  pivotX,
                  pivotY,
                  barWidth,
                  endingStyle,
                  isLessThan
                }
              },
              'progress'
            );
            // 最后绘制在画布上
            ctx.drawImage(offscreenCanvas2, 0, 0);
          }

          // 绘制指示器
          if (indicatorImageEl) {
            const { canvas: offscreenCanvas3 } = this.getOffscreen(
              {
                width: indicatorImageElWidth,
                height: indicatorImageElHeight,
                init: (_, ctx) => {
                  this.drawRecolorImage(ctx, indicatorImageEl, {
                    x: 0,
                    y: 0
                  });
                },
                dirtyParmas: {
                  indicatorImageSrc: indicatorImageSrc ?? ''
                }
              },
              'indicator'
            );

            const centerX = pivotX;
            const centerY = pivotY;
            const radius =
              indicatorRadius != undefined ? indicatorRadius : barRadius;

            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate(toRadian(angleEnd + 90));
            ctx.translate(-centerX, -centerY);

            const _x = centerX - indicatorImageEl.width / 2;
            const _y = centerY - radius - indicatorImageEl.height / 2;
            ctx.drawImage(offscreenCanvas3, _x, _y);

            ctx.restore();
          }
        },
        dirtyParmas: {
          key: Math.random()
        }
      },
      'arc_progress'
    );

    // ctx.drawImage(offscreenCanvas, x, y);
    this.drawAniFrame(offscreenCanvas);

    const { width, height } = this.getSize();
    this.setRuntimeData({
      width,
      height,
      rotation: 0
    });
    ctx.restore();
  }
}
